import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import { useAuth } from './AuthContext';
import { AuthStage } from './types';
import { sendEmailCode, verifyCode } from './api';
import './Login.css';
import {translatesSendEmailCodeError, translatesVerifyCodeError} from "./errorMessages";
import {useData} from "../Data/DataContext";
import { useNotification } from "../Utils/NotificationContext";

const Login: React.FC = () => {
  const { authData, setEmail, setCode, setStage, setLoading, setError } = useAuth();
  const {refreshData} = useData()
  const { showNotification } = useNotification();
  const [emailInput, setEmailInput] = useState(authData.email || '');
  const [codeInput, setCodeInput] = useState('');
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Countdown für Code-Ablauf und Resend-Button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
  }, [countdown, resendDisabled]);

  // Countdown für Code-Ablauf
  useEffect(() => {
    if (codeExpiresAt) {
      const updateRemainingTime = () => {
        const now = Date.now();
        if (now >= codeExpiresAt) {
          showNotification('Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.', 'warning');
          setCodeExpiresAt(null);
          return;
        }
      };

      updateRemainingTime();
      const interval = setInterval(updateRemainingTime, 10000); // Alle 10 Sekunden prüfen

      return () => clearInterval(interval);
    }
  }, [codeExpiresAt]);

  // Email-Formular-Handler
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput || !emailInput.trim()) {
      showNotification('Bitte geben Sie Ihre E-Mail-Adresse ein.', 'warning');
      return;
    }

    setLoading(true);

    try {
      const response = await sendEmailCode(emailInput.trim());

      if (response.success) {
        setEmail(emailInput.trim());
        setStage(AuthStage.CODE_INPUT);
        if (response.expiresAt) {
          setCodeExpiresAt(response.expiresAt);
        }

        // Resend-Button für 30 Sekunden deaktivieren
        setResendDisabled(true);
        setCountdown(30);
      } else {
        showNotification(translatesSendEmailCodeError(response.errorCode), 'danger');
      }
    } catch (err) {
      showNotification('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.', 'danger');
      console.error('Login-Fehler:', err);
    } finally {
      setLoading(false);
    }
  };

  // Code-Formular-Handler
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeInput || !codeInput.trim()) {
      showNotification('Bitte geben Sie den Code ein.', 'warning');
      return;
    }

    setLoading(true);

    try {
      const response = await verifyCode(authData.email, codeInput.trim());

      if (response.success) {
        console.log('Login: Erfolgreiche Anmeldung');
        setCode(codeInput.trim());
        setStage(AuthStage.AUTHENTICATED);
        await refreshData()
      } else {
        showNotification(translatesVerifyCodeError(response.errorCode), 'danger');
      }
    } catch (err) {
      showNotification('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.', 'danger');
      console.error('Code-Verifizierungsfehler:', err);
    } finally {
      setLoading(false);
    }
  };

  // Code erneut senden
  const handleResendCode = async () => {
    if (resendDisabled) return;

    setLoading(true);

    try {
      const response = await sendEmailCode(authData.email);

      if (response.success) {
        if (response.expiresAt) {
          setCodeExpiresAt(response.expiresAt);
        }
        showNotification('Ein neuer Code wurde an Ihre E-Mail-Adresse gesendet.', 'success');

        // Resend-Button für 30 Sekunden deaktivieren
        setResendDisabled(true);
        setCountdown(30);
      } else {
        showNotification(translatesSendEmailCodeError(response.errorCode), 'danger');
      }
    } catch (err) {
      showNotification('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.', 'danger');
      console.error('Code-Resend-Fehler:', err);
    } finally {
      setLoading(false);
    }
  };

  // Zurück zum Email-Eingabe-Schritt
  const handleBackToEmail = () => {
    setStage(AuthStage.EMAIL_INPUT);
  };

  return (
    <div className="login-container">
      <Card className="login-card">
        <Card.Body>
          <Card.Title className="text-center mb-4">
            {authData.stage === AuthStage.EMAIL_INPUT ? 'Anmelden' : 'Code eingeben'}
          </Card.Title>

          {authData.stage === AuthStage.EMAIL_INPUT ? (
            <Form onSubmit={handleEmailSubmit}>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>E-Mail-Adresse</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="name@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  disabled={authData.isLoading}
                  autoFocus
                  required
                />
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-100" 
                disabled={authData.isLoading}
              >
                {authData.isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Wird gesendet...</span>
                  </>
                ) : 'Code senden'}
              </Button>
            </Form>
          ) : (
            <Form onSubmit={handleCodeSubmit}>
              <p className="text-muted mb-3">
                Wir haben einen Code an <strong>{authData.email}</strong> gesendet.
              </p>

              <Form.Group className="mb-3" controlId="code">
                <Form.Label>Bestätigungscode</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="8-stelliger Code"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  disabled={authData.isLoading}
                  autoFocus
                  maxLength={8}
                  required
                />
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 mb-3" 
                disabled={authData.isLoading}
              >
                {authData.isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Wird überprüft...</span>
                  </>
                ) : 'Anmelden'}
              </Button>

              <div className="d-flex justify-content-between">
                <Button 
                  variant="link" 
                  onClick={handleBackToEmail} 
                  disabled={authData.isLoading}
                >
                  E-Mail ändern
                </Button>

                <Button 
                  variant="link" 
                  onClick={handleResendCode} 
                  disabled={authData.isLoading || resendDisabled}
                >
                  {resendDisabled && countdown > 0 ? `Neuer Code in ${countdown}s` : 'Code erneut senden'}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Login;
